import React from 'react';
import { Link } from 'react-router-dom';

const CAREER_START_YEAR = 2015;

// Header Component
export const Header = () => {
  return (
    <header className="bg-white shadow-sm border-b">
      <div className="flex justify-between items-center px-6 py-3">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-bold">S</span>
          </div>
          <span className="text-lg font-semibold text-gray-800">Sowmya Mind Care</span>
        </div>
        
        <nav className="hidden md:flex space-x-8">
          <a href="/#telepsychiatry" className="text-gray-600 hover:text-blue-600 transition-colors">International Patients</a>
          <a href="/#research" className="text-gray-600 hover:text-blue-600 transition-colors">Research</a>
          <a href="/#clinic-photos" className="text-gray-600 hover:text-blue-600 transition-colors">Clinic</a>
          <a href="/#reviews" className="text-gray-600 hover:text-blue-600 transition-colors">Testimonials</a>
          <a href="/#contact" className="text-gray-600 hover:text-blue-600 transition-colors">Contact Us</a>
        </nav>
      </div>

      <div className="bg-gray-50 px-6 py-2">
        <nav className="flex space-x-8">
          <a href="/" className="text-gray-700 hover:text-blue-600 transition-colors">Home</a>
          <a href="/#profile" className="text-gray-700 hover:text-blue-600 transition-colors">About</a>
          <a href="/#specialties" className="text-gray-700 hover:text-blue-600 transition-colors">Specialties</a>
          <a href="/#research" className="text-gray-700 hover:text-blue-600 transition-colors">Research</a>
          <a href="/#reviews" className="text-gray-700 hover:text-blue-600 transition-colors">Reviews</a>
          <a href="/#contact" className="text-gray-700 hover:text-blue-600 transition-colors">Contact</a>
        </nav>
      </div>
    </header>
  );
};

// Hero Section Component
export const HeroSection = () => {
  return (
    <div
      className="relative h-64 bg-cover bg-center flex items-center justify-center"
      style={{
        backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.8), rgba(30, 58, 138, 0.8)), url('https://images.pexels.com/photos/4101143/pexels-photo-4101143.jpeg')`,
      }}
    >
      <div className="text-center text-white">
        <h1 className="hero-title text-4xl md:text-5xl font-bold mb-2">Dr. Naga Sowmya Garapati</h1>
        <p className="hero-subtitle text-xl md:text-2xl">Consultant Psychiatrist &amp; Founder, Sowmya Mind Care | NIMHANS Alumna</p>
      </div>
    </div>
  );
};

// Doctor Profile Component
export const DoctorProfile = () => {
  const experienceYears = new Date().getFullYear() - CAREER_START_YEAR;

  return (
    <div id="profile" className="max-w-7xl mx-auto px-6 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <img
              src="/sowmya_pic.jpeg"
              alt="Dr. Naga Sowmya Garapati"
              className="w-full h-80 object-cover"
            />
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Dr. Naga Sowmya Garapati</h2>
              <p className="text-gray-600 mb-4">Consultant Psychiatrist &amp; Founder, Sowmya Mind Care</p>
              <p className="text-gray-600 text-sm">Psychiatrist with over {experienceYears} years of clinical experience</p>
              <p className="text-gray-500 text-sm mt-1">JP Nagar, Bengaluru · Clinic &amp; Online</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <p className="text-gray-700 leading-relaxed text-lg">
              Dr. Naga Sowmya Garapati is a Consultant Psychiatrist and the Founder of Sowmya Mind Care, a dedicated psychiatric OPD clinic located in JP Nagar, Bengaluru. She completed her MBBS from NRI Medical College, Guntur, followed by DPM from Guntur Medical College and DNB (Psychiatry) from Spandana Institute of DNB, Bengaluru, and further pursued a Post-Doctoral Fellowship in Telepsychiatry from NIMHANS.
            </p>
            <br />
            <p className="text-gray-700 leading-relaxed text-lg">
              With over {experienceYears} years of overall clinical experience, Dr. Sowmya has worked across diverse academic and clinical settings — including roles as Senior Resident and Assistant Professor at Shridevi Medical College, Tumkur, and as Consultant Psychiatrist and PDF fellow at NIMHANS, Bangalore. Her experience spans the assessment and management of a wide range of neuropsychiatric disorders across all age groups.
            </p>
            <br />
            <p className="text-gray-700 leading-relaxed text-lg">
              Her clinical interests include general psychiatry, psycho-oncology, women's mental health, sexual medicine, disaster mental health, and child &amp; adolescent psychiatry. She has also been a part of the <strong>NIMHANS Archival Digital Initiative</strong>, All India Institute of Mental Health — contributing to the preservation and digital documentation of India's psychiatric heritage. She strongly believes that mental health care is most effective when it is person-centred, stigma-free, and tailored to each individual's life context. At Sowmya Mind Care, her goal is to create a safe, respectful space where individuals feel heard, understood, and supported on their journey toward better mental health and overall wellbeing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Qualifications Component
export const Qualifications = () => {
  const qualifications = [
    {
      degree: "MBBS",
      institution: "NRI Medical College, Guntur — NTR University of Health Sciences",
      year: "2010 – 2016"
    },
    {
      degree: "DPM (Diploma in Psychological Medicine)",
      institution: "Guntur Medical College, Andhra Pradesh",
      year: "2016 – 2018"
    },
    {
      degree: "DNB Psychiatry",
      institution: "Spandana DNB Institute, Bangalore",
      year: "2019 – 2021"
    },
    {
      degree: "Senior Residency — Psychiatry",
      institution: "Sri Devi Institute of Medical Sciences & Research Hospital, Tumkur",
      year: "2021 – 2023"
    },
    {
      degree: "Assistant Professor — Psychiatry",
      institution: "Sridevi Institute of Medical Sciences & Research Hospital, Tumkur",
      year: "2023"
    },
    {
      degree: "Post-Doctoral Fellowship (PDF) in Telepsychiatry",
      institution: "NIMHANS, Bangalore",
      year: "2024 – 2025"
    }
  ];

  return (
    <div id="qualifications" className="bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-3xl font-bold text-gray-800 mb-8">Qualifications</h2>
        <div className="bg-white rounded-lg shadow-lg p-8">
          <ul className="space-y-4">
            {qualifications.map((qual, index) => (
              <li key={index} className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-gray-200 pb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">{qual.degree}</h3>
                  <p className="text-gray-600">{qual.institution}</p>
                </div>
                <span className="text-blue-600 font-semibold mt-2 md:mt-0">{qual.year}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// Clinic Photos Component
export const ClinicPhotos = () => {
  const photos = [
    {
      src: "/clinic_photo_1.jpg",
      alt: "Sowmya Mind Care clinic consultation room"
    },
    {
      src: "/clinic_photo_2.jpg",
      alt: "Sowmya Mind Care clinic interior"
    },
    {
      src: "/clinic_photo_3.jpg",
      alt: "Sowmya Mind Care clinic"
    }
  ];

  return (
    <div id="clinic-photos" className="py-12 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-3xl font-bold text-gray-800 mb-8">Clinic Photos</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {photos.map((photo, index) => (
            <div key={index} className="rounded-lg overflow-hidden shadow-lg">
              <img
                src={photo.src}
                alt={photo.alt}
                className="w-full h-56 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-gray-500 mt-4">
          Photos from{' '}
          <a
            href="https://maps.app.goo.gl/sowmyamindcare"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            Google Maps
          </a>
        </p>
      </div>
    </div>
  );
};

// Google Reviews Component
export const GoogleReviews = () => {
  const reviews = [
    {
      name: "Bommula Gangadhar",
      stars: 5,
      date: "7 months ago",
      text: "The clinic itself is calm and well-organized. The support staff were courteous and efficient, and appointments started on time. She takes time to explore not just symptoms, but also underlying factors like lifestyle, stressors, and emotional health. Treatment plans are realistic, and she discusses options (medication, therapy, self-care) in a way that makes you feel involved in your healing journey. I would highly recommend her to anyone seeking a psychiatrist who genuinely cares and provides comprehensive, balanced care."
    },
    {
      name: "kalyan reddy",
      stars: 5,
      date: "7 months ago",
      text: "I can't say enough positive things about Dr. Sowmya. From the very first appointment, I felt truly heard and supported. They take the time to listen carefully, ask thoughtful questions, and work collaboratively to find the best treatment plan. I appreciated how compassionate, non-judgmental, and professional they were throughout the process. She helped me gain real insight into my mental health, and I've seen meaningful improvement in both my day-to-day wellbeing and long-term outlook."
    },
    {
      name: "Sindhuri Yerra",
      stars: 5,
      date: "6 months ago",
      text: "Dr Sowmya is an empathetic and highly skilled psychiatrist who listens patiently and makes you feel truly understood. She provided clear guidance with practical treatment options that brought noticeable improvement. A rare mix of professionalism and compassion, highly recommended."
    },
    {
      name: "Nireesha Anne",
      stars: 5,
      date: "7 months ago",
      text: "The clinic is known for its excellent service and welcoming atmosphere. Dr. Sowmya is highly professional, caring, and always dedicated to patients' well-being. Truly appreciate the good work!"
    },
    {
      name: "Manasa Karri",
      stars: 5,
      date: "6 months ago",
      text: "I would highly recommend this clinic. You critically analyze and diagnose with precision, prescribing necessary treatment and your holistic approach has truly worked wonders for me. Your calm demeanor, patient-friendly attitude, and empathetic understanding of my symptoms — I am very happy with the conversation I had."
    },
    {
      name: "Lakshmi Sava",
      stars: 5,
      date: "9 months ago",
      text: "This clinic has a caring psychiatrist who listens patiently and explains treatment clearly. The clinic is well-maintained. A great choice for mental health support!"
    },
    {
      name: "santosh sutrave",
      stars: 5,
      date: "8 months ago",
      text: "Dr. Sowmya is a compassionate and skilled psychiatrist. She listens patiently, explains things clearly, and makes you feel truly supported. Highly recommended."
    },
    {
      name: "Nagasree Kuchipudi",
      stars: 5,
      date: "7 months ago",
      text: "Good treatment. This place treats you not like a patient but like a friend or family member. Best doctor to consult for psychiatric problems."
    },
    {
      name: "Pulapa Subba Rao",
      stars: 5,
      date: "7 months ago",
      text: "Highly talented Psychiatrist and apt advice. — Prof. Pulapa Subba Rao, Former Vice Chancellor"
    },
    {
      name: "sridevi mutyala",
      stars: 5,
      date: "7 months ago",
      text: "Dr. Sowmya is incredibly knowledgeable and took the time to answer all my questions. Highly recommend."
    },
    {
      name: "Rama Devi Pulapa",
      stars: 5,
      date: "7 months ago",
      text: "Dr Sowmya is an excellent and ultra modern psychiatrist. Efficient in treatment."
    },
    {
      name: "Sree katta",
      stars: 5,
      date: "7 months ago",
      text: "Had a very great experience with this doctor. Treated very well."
    },
    {
      name: "Shaam Varma",
      stars: 5,
      date: "7 months ago",
      text: "Dr. Sowmya is very polite and knowledgeable. Had a great experience."
    },
    {
      name: "salwa farooq",
      stars: 5,
      date: "7 months ago",
      text: "Very compassionate and knowledgeable psychiatrist."
    },
    {
      name: "Keshava G",
      stars: 5,
      date: "5 months ago",
      text: "Perfect doctor for mind care. Thanks a lot."
    },
    {
      name: "Venkatalakshmi B",
      stars: 5,
      date: "7 months ago",
      text: "Doctor is very polite and friendly."
    },
    {
      name: "Chinmay R",
      stars: 5,
      date: "7 months ago",
      text: "Nice doctor and very comfortable."
    },
    {
      name: "sai phani deep",
      stars: 5,
      date: "7 months ago",
      text: "Good experience."
    }
  ];

  return (
    <div id="reviews" className="py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h2 className="text-3xl font-bold text-gray-800">Patient Reviews</h2>
          <a
            href="https://www.google.com/maps/place/Sowmya+Mind+Care+-+Dr.+Naga+Sowmya+Psychiatrist/@12.8920093,77.5802163,17z"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm hover:shadow-md transition-shadow"
          >
            <span className="text-2xl font-bold text-gray-800">5.0</span>
            <span className="text-yellow-400 text-xl">★★★★★</span>
            <span className="text-gray-500 text-sm">51 reviews on Google</span>
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                    {review.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{review.name}</p>
                    <p className="text-gray-400 text-xs">{review.date}</p>
                  </div>
                </div>
                <span className="text-yellow-400 text-sm">{'★'.repeat(review.stars)}</span>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">{review.text}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <a
            href="https://www.google.com/maps/place/Sowmya+Mind+Care+-+Dr.+Naga+Sowmya+Psychiatrist/@12.8920093,77.5802163,17z"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
          >
            See all 51 reviews on Google Maps
          </a>
        </div>
      </div>
    </div>
  );
};

// Research Interests Component
export const ResearchInterests = () => {
  const dissertations = [
    "Assessment and management of neuropsychiatric disorders across age groups",
    "Psycho-oncology: mental health support in cancer care",
    "Telepsychiatry and digital mental health delivery",
    "Women's mental health across the reproductive lifespan",
    "Child and adolescent psychiatric care",
    "Stigma reduction and community mental health awareness"
  ];

  const researchInterests = [
    { label: "General Psychiatry & Evidence-Based Care", slug: "general-psychiatry" },
    { label: "Psycho-oncology", slug: "psycho-oncology" },
    { label: "Women's Mental Health", slug: "womens-mental-health" },
    { label: "Child & Adolescent Psychiatry", slug: "child-adolescent-psychiatry" },
    { label: "Telepsychiatry and Digital Mental Health", slug: "telepsychiatry" },
    { label: "Geriatric Mental Health", slug: "geriatric-mental-health" },
    { label: "Sexual Medicine", slug: "sexual-medicine" },
    { label: "Disaster Mental Health", slug: "disaster-mental-health" },
    { label: "Relationship Mental Health", slug: "relationship-mental-health" },
    { label: "Behavioral Health & Psychotherapy", slug: "behavioral-health" },
    { label: "Neuropsychiatric Disorders", slug: "neuropsychiatric-disorders" },
    { label: "Mental Health Awareness and Community Programs", slug: "mental-health-awareness" }
  ];

  return (
    <div id="research" className="bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Dissertations and Thesis</h2>
            <ul className="space-y-3">
              {dissertations.map((item, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-blue-500 mr-3 mt-1">•</span>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Research Interests</h2>
            <ul className="space-y-3">
              {researchInterests.map((item, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-blue-500 mr-3 mt-1">•</span>
                  <Link
                    to={`/specialty/${item.slug}`}
                    className="text-gray-700 hover:text-blue-600 hover:underline transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Specialties Component
export const Specialties = () => {
  const specialties = [
    {
      name: "Women's Mental Health",
      slug: "womens-mental-health",
      image: "https://images.pexels.com/photos/7699511/pexels-photo-7699511.jpeg",
      description: "Reproductive psychiatry, perinatal mental health, and care across every stage of a woman's life"
    },
    {
      name: "Child & Adolescent Psychiatry",
      slug: "child-adolescent-psychiatry",
      image: "https://images.unsplash.com/photo-1650504149601-f9fdd445c187?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxjaGlsZCUyMHBzeWNob2xvZ3l8ZW58MHx8fHwxNzQ5OTY5OTQxfDA&ixlib=rb-4.1.0&q=85",
      description: "Mental health care tailored for children and teenagers with ADHD, ASD, anxiety, and more"
    },
    {
      name: "Neuropsychiatric Disorders",
      slug: "neuropsychiatric-disorders",
      image: "https://images.unsplash.com/photo-1636249253913-40e83d5423e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxhbnhpZXR5JTIwdGhlcmFweXxlbnwwfHx8fDE3NDk5Njk5Mzd8MA&ixlib=rb-4.1.0&q=85",
      description: "Schizophrenia, bipolar disorder, OCD, and complex disorders at the neurology-psychiatry interface"
    },
    {
      name: "Telepsychiatry",
      slug: "telepsychiatry",
      image: "https://images.pexels.com/photos/7467939/pexels-photo-7467939.jpeg",
      description: "Post-doctoral expertise in digital mental health — quality care delivered online, anywhere in India"
    },
    {
      name: "Behavioral Health & Psychotherapy",
      slug: "behavioral-health",
      image: "https://images.unsplash.com/photo-1607619056574-7b8d3ee536b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxhZGRpY3Rpb24lMjB0cmVhdG1lbnR8ZW58MHx8fHwxNzQ5OTY5OTUwfDA&ixlib=rb-4.1.0&q=85",
      description: "CBT, DBT, EMDR, and evidence-based psychological treatments for lasting change"
    },
    {
      name: "Mental Health Awareness",
      slug: "mental-health-awareness",
      image: "https://images.unsplash.com/photo-1698284866504-631a025548e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxnZXJpYXRyaWMlMjBtZW50YWwlMjBoZWFsdGh8ZW58MHx8fHwxNzQ5OTY5OTU1fDA&ixlib=rb-4.1.0&q=85",
      description: "Community education, stigma reduction, and making mental health accessible for all"
    },
    {
      name: "Psycho-oncology",
      slug: "psycho-oncology",
      image: "https://images.pexels.com/photos/3985163/pexels-photo-3985163.jpeg",
      description: "Integrated psychiatric support for cancer patients, survivors, and their families"
    },
    {
      name: "Geriatric Mental Health",
      slug: "geriatric-mental-health",
      image: "https://images.pexels.com/photos/3768131/pexels-photo-3768131.jpeg",
      description: "Specialised care for older adults — dementia, late-life depression, and cognitive health"
    },
    {
      name: "Sexual Medicine",
      slug: "sexual-medicine",
      image: "https://images.pexels.com/photos/7551659/pexels-photo-7551659.jpeg",
      description: "Compassionate, evidence-based care for sexual health, desire disorders, and intimacy concerns"
    },
    {
      name: "Disaster Mental Health",
      slug: "disaster-mental-health",
      image: "https://images.pexels.com/photos/6624258/pexels-photo-6624258.jpeg",
      description: "Psychological first aid and trauma recovery for individuals affected by crisis events"
    },
    {
      name: "Relationship Mental Health",
      slug: "relationship-mental-health",
      image: "https://images.pexels.com/photos/4587998/pexels-photo-4587998.jpeg",
      description: "Psychiatric and psychotherapeutic support for relationship wellbeing and intimacy challenges"
    },
    {
      name: "General Psychiatry",
      slug: "general-psychiatry",
      image: "https://images.pexels.com/photos/5699456/pexels-photo-5699456.jpeg",
      description: "Comprehensive evidence-based assessment and treatment for the full range of psychiatric conditions"
    }
  ];

  return (
    <div id="specialties" className="py-12">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Specialties</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {specialties.map((specialty, index) => (
            <Link
              key={index}
              to={`/specialty/${specialty.slug}`}
              className="bg-white rounded-lg shadow-lg overflow-hidden specialty-card cursor-pointer block hover:shadow-xl transition-shadow"
            >
              <div className="h-48 bg-cover bg-center image-overlay" style={{ backgroundImage: `url(${specialty.image})` }}></div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">{specialty.name}</h3>
                <p className="text-gray-600 text-sm">{specialty.description}</p>
                <span className="inline-block mt-3 text-blue-600 text-sm font-medium hover:underline">Learn more →</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

// Contact Section Component
export const ContactSection = () => {
  return (
    <div
      id="contact"
      className="bg-cover bg-center py-16"
      style={{
        backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.9), rgba(30, 58, 138, 0.9)), url('https://images.pexels.com/photos/7579319/pexels-photo-7579319.jpeg')`,
      }}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 text-white">
          <div>
            <h3 className="text-xl font-bold mb-4">How to contact us</h3>
            <div className="space-y-2">
              <p>Sowmya Mind Care</p>
              <p className="text-sm">JP Nagar, Bengaluru, Karnataka, India</p>
              <p className="text-sm">Available: Clinic &amp; Online Consultations</p>
              <a
                href="https://www.linkedin.com/in/dr-naga-sowmya-garapati-71520a148/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm underline block mt-2"
              >
                LinkedIn Profile
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4">Our Doctor</h3>
            <ul className="space-y-2 text-sm">
              <li>Dr. Naga Sowmya Garapati</li>
              <li className="text-blue-200">Consultant Psychiatrist</li>
              <li className="text-blue-200">NIMHANS Alumna</li>
              <li className="text-blue-200">DNB Psychiatry</li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-xl font-bold mb-4">Specialities</h3>
            <ul className="space-y-2 text-sm">
              <li>Depression</li>
              <li>Anxiety Disorders</li>
              <li>PTSD Therapy</li>
              <li>Child Psychology</li>
              <li>Addiction Treatment</li>
              <li>Geriatric Psychiatry</li>
              <li>Bipolar Disorder</li>
              <li>Cognitive Therapy</li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-xl font-bold mb-4">Our Locations</h3>
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-semibold">Sowmya Mind Care Clinic</h4>
                <p>JP Nagar</p>
                <p>Bengaluru, Karnataka, India</p>
              </div>
              <div>
                <h4 className="font-semibold">Teleconsultation</h4>
                <p>Available Online</p>
                <p>India-wide</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Footer Component
export const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h4 className="font-bold mb-4">EOS Patients</h4>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>International Patients</li>
              <li>Treatment Programs</li>
              <li>Insurance Coverage</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold mb-4">Media</h4>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>Press Releases</li>
              <li>Publications</li>
              <li>Research Papers</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold mb-4">Testimonials</h4>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>Patient Stories</li>
              <li>Success Cases</li>
              <li>Reviews</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold mb-4">About Us</h4>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>Our Mission</li>
              <li>Our Team</li>
              <li>Careers</li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-700 mt-8 pt-8 text-center">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} Sowmya Mind Care. All rights reserved. | Privacy Policy | Terms of Service
          </p>
        </div>
      </div>
    </footer>
  );
};